import { RECIPE_INITIAL_VALUES } from "@/pages/sy/models/Recipe";
import type { Recipe } from "@/pages/sy/models/Recipe";
import type { RecipeLayer } from "@/pages/sy/models/RecipeLayer";
import { useRecipeLayersQuery } from "@/pages/sy/hooks/queries/useRecipeLayersQuery";
import {
  useUpsertRecipeMutation,
  useUpsertRecipeLayerMutation,
  useDeleteRecipeLayerMutation,
  useUpgradeRecipeMutation,
  useSetRecipeFamilyEnableMutation,
  useReorderRecipeLayerMutation,
} from "@/pages/sy/hooks/queries/useRecipeMutations";
import { useCommoditiesQuery } from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import { getErrorMessage } from "@/shared/hooks/getErrorMessage";
import { CommentsField } from "@/shared/fields/CommentsField";
import {
  ErrorModal,
  MesfModal,
  dxtToLocalServerTime,
} from "@dexteel/mesf-core";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Warning as WarningIcon } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { RecipeLayerComponent } from "./RecipeLayer";
import { RecipeLayerModal } from "./RecipeLayerModal";
import { SortableRecipeLayer } from "./SortableRecipeLayer";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { MODAL_STATE } from "../models/recipe-types";
import type { ModalState, ModalHideParams } from "../models/recipe-types";

interface Props {
  recipe: Recipe | null;
  recipes: Recipe[];
  modal: ModalState;
  onHide: (params: ModalHideParams) => void;
}

export const RecipeDetails = ({ recipe, recipes, modal, onHide }: Props) => {
  const { data: allCommodities = [] } = useCommoditiesQuery();
  const enabledCommodities = useMemo(
    () => allCommodities.filter((c) => c.IsEnable),
    [allCommodities],
  );

  // === TanStack Query Mutations ===
  const upsertRecipeMutation = useUpsertRecipeMutation();
  const upsertLayerMutation = useUpsertRecipeLayerMutation();
  const deleteLayerMutation = useDeleteRecipeLayerMutation();
  const upgradeMutation = useUpgradeRecipeMutation();
  const setFamilyEnableMutation = useSetRecipeFamilyEnableMutation();
  const reorderLayerMutation = useReorderRecipeLayerMutation();
  const queryClient = useQueryClient();

  const [error, setError] = useState("");
  const [message, setNotificationMessage] = useState<string>("");
  const onMutationError = (err: unknown) => setError(getErrorMessage(err));
  const [newLayerId, setNewLayerId] = useState<number | null>(null);
  const [currentRecipe, setCurrentRecipe] = useState<Recipe | null>(null);
  const [showRecipeLayerModal, setShowRecipeLayerModal] =
    useState<boolean>(false);
  const [selectedCharge, setSelectedCharge] = useState<number>(1);

  // State for "Recipe In Use" confirmation modal
  const [showUpgradeConfirmModal, setShowUpgradeConfirmModal] = useState(false);
  const [pendingRecipeData, setPendingRecipeData] = useState<Recipe | null>(
    null,
  );
  // State for "Rename Family" confirmation modal (when recipe is NOT in use)
  const [showRenameConfirmModal, setShowRenameConfirmModal] = useState(false);
  const [pendingRenameValue, setPendingRenameValue] = useState<string | null>(
    null,
  );
  // For adding new layers when recipe is in use
  const [pendingNewLayerData, setPendingNewLayerData] = useState<{
    commodityId: number;
    weight: number;
    chargeNumber: number;
  } | null>(null);
  // For updating existing layers when recipe is in use
  const [pendingLayerUpdateData, setPendingLayerUpdateData] =
    useState<RecipeLayer | null>(null);
  // State for Enable checkbox confirmation modal
  const [toggleEnableConfirm, setToggleEnableConfirm] = useState<{
    open: boolean;
    isEnable: boolean;
  }>({ open: false, isEnable: false });

  // Combined loading state for UI (replaces manual isLoading/isSaving states)
  const isLoading =
    upsertLayerMutation.isPending || deleteLayerMutation.isPending;
  const isSaving = upsertRecipeMutation.isPending;
  // Track the effective recipeId (from prop or form for newly created recipes)
  const [effectiveRecipeId, setEffectiveRecipeId] = useState<number | null>(
    recipe?.RecipeId ?? null,
  );

  // === TanStack Query: Recipe Layers ===
  const { data: rawRecipeLayers = [] } = useRecipeLayersQuery(
    effectiveRecipeId,
    selectedCharge,
  );

  const recipeLayers = useMemo(
    () =>
      [...rawRecipeLayers].sort(
        (a, b) => (b.LayerNumber ?? 0) - (a.LayerNumber ?? 0),
      ),
    [rawRecipeLayers],
  );

  const totalWeight = useMemo(
    () => rawRecipeLayers.reduce((sum, layer) => sum + (layer.Weight || 0), 0),
    [rawRecipeLayers],
  );

  const maxLayerNumber =
    recipeLayers?.length > 0
      ? Math.max(...recipeLayers.map((l) => l.LayerNumber ?? 0))
      : 0;

  // === Drag & Drop ===
  const [dragLayers, setDragLayers] = useState<RecipeLayer[] | null>(null);

  // Layers to render: local drag state takes priority over server state
  const displayLayers = dragLayers ?? recipeLayers;

  // Visual reorder during hover (no backend call)
  const handleMoveLayer = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setDragLayers((prev) => {
        const current = prev ?? recipeLayers;
        if (!current) return prev;
        if (dragIndex < 0 || dragIndex >= current.length) return prev;
        if (hoverIndex < 0 || hoverIndex >= current.length) return prev;

        const newOrder = [...current];
        const [removed] = newOrder.splice(dragIndex, 1);
        newOrder.splice(hoverIndex, 0, removed);
        return newOrder;
      });
    },
    [recipeLayers],
  );

  // Persist to backend when drop ends (called from useDrag.end)
  const handleDragEnd = useCallback(
    (layerId: number, finalIndex: number) => {
      // List is sorted DESCENDING (highest LayerNumber first)
      // finalIndex 0 = top = highest LayerNumber
      const filteredLayers = (dragLayers ?? recipeLayers)?.filter(
        (l) => l.ChargeNumber === selectedCharge,
      );
      const totalLayers = filteredLayers?.length ?? 0;
      const newLayerNumber = totalLayers - finalIndex;

      // Optimistic update: write new order into TanStack Query cache
      // so when dragLayers clears, recipeLayers already has the correct order
      if (dragLayers) {
        queryClient.setQueryData<RecipeLayer[]>(
          ["recipe-layers", effectiveRecipeId, selectedCharge],
          dragLayers.map((layer, index) => ({
            ...layer,
            LayerNumber: dragLayers.length - index,
          })),
        );
      }

      // Clear drag state immediately — cache now reflects the new order
      setDragLayers(null);

      // Fire mutation (backend confirms via SignalR refetch)
      reorderLayerMutation.mutate(
        { recipeLayerId: layerId, newLayerNumber },
        {
          onError: () => {
            // Rollback: invalidate to refetch real server state
            queryClient.invalidateQueries({
              queryKey: ["recipe-layers", effectiveRecipeId, selectedCharge],
            });
            setNotificationMessage("Failed to reorder layer");
          },
        },
      );
    },
    [
      dragLayers,
      recipeLayers,
      selectedCharge,
      effectiveRecipeId,
      reorderLayerMutation,
      queryClient,
    ],
  );

  // Rollback if dropped outside valid target
  const handleDragCancel = useCallback(() => {
    setDragLayers(null);
  }, []);

  // Check if ANY version of the recipe family is in use (disables RecipeName editing)
  const isFamilyInUse = useMemo(() => {
    if (!recipe?.OriginalRecipeId) return false;
    return recipes.some(
      (r) => r.OriginalRecipeId === recipe.OriginalRecipeId && r.IsUsed,
    );
  }, [recipe?.OriginalRecipeId, recipes]);

  const {
    reset,
    getValues,
    control,
    formState: { errors },
  } = useForm<Recipe>({
    mode: "onChange",
    defaultValues: RECIPE_INITIAL_VALUES,
  });

  const isNewRecipe = modal === MODAL_STATE.NEW;

  const formRecipeId = getValues("RecipeId");
  const isNewRecipePending =
    isNewRecipe && !currentRecipe?.RecipeId && !formRecipeId;

  // IsUsed recipes are frozen — already used in a charge (Open, Closed, or Charged)
  const isReadonly = Boolean(recipe?.IsUsed);

  const handleAddLayer = () => {
    if (isNewRecipePending) {
      const recipeData = {
        ...getValues(),
        RecipeName: getValues("RecipeName").trim(),
      };

      // Validation: name required (form also validates this)
      if (!recipeData.RecipeName) return;

      upsertRecipeMutation.mutate(recipeData, {
        onSuccess: (resp) => {
          const entityId = resp.data?.tables?.[0]?.rows?.[0]?.Id;
          const updatedRecipe = {
            ...recipeData,
            RecipeId: entityId,
            IsEnable: false,
          };
          reset(updatedRecipe);
          setCurrentRecipe(updatedRecipe);
          setEffectiveRecipeId(entityId);
          setNotificationMessage("Recipe created successfully.");
          setShowRecipeLayerModal(true);
        },
        onError: onMutationError,
      });
    } else if (getValues("RecipeId") || recipe?.RecipeId) {
      setShowRecipeLayerModal(true);
    }
    // If no recipeId, button is disabled anyway
  };

  const handleFieldBlur = (
    field: keyof Recipe,
    value: Recipe[keyof Recipe],
  ) => {
    if (isLoading || isSaving) return;
    if (
      isNewRecipe &&
      field === "RecipeName" &&
      typeof value === "string" &&
      value.trim()
    ) {
      const recipeData = {
        ...RECIPE_INITIAL_VALUES,
        RecipeName: value?.trim(),
      };
      upsertRecipeMutation.mutate(recipeData, {
        onSuccess: (resp) => {
          const entityId = resp.data?.tables?.[0]?.rows?.[0]?.Id;
          const updatedRecipe = {
            ...recipeData,
            RecipeId: entityId,
            IsEnable: false,
          };
          reset(updatedRecipe);
          setCurrentRecipe(updatedRecipe);
          setEffectiveRecipeId(entityId);
          setNotificationMessage("Recipe created successfully.");
          onHide({
            shouldUpdate: true,
            close: false,
            entityId,
            keepEditMode: true,
          });
        },
        onError: onMutationError,
      });
    } else if (
      (!isNewRecipe || getValues("RecipeId")) &&
      (recipe?.RecipeId || getValues("RecipeId"))
    ) {
      // Check if renaming an existing recipe - show confirmation modal
      const trimmedValue = typeof value === "string" ? value.trim() : value;
      if (
        field === "RecipeName" &&
        typeof trimmedValue === "string" &&
        trimmedValue !== recipe?.RecipeName
      ) {
        setPendingRenameValue(trimmedValue);
        setShowRenameConfirmModal(true);
        return;
      }

      const recipeData = {
        ...getValues(),
        [field]: value,
        RecipeId: getValues("RecipeId") || recipe?.RecipeId,
      } as Recipe;
      upsertRecipeMutation.mutate(recipeData, {
        onSuccess: (resp) => {
          const result = resp.data?.tables?.[0]?.rows?.[0];
          // Check if recipe is in use - show confirmation modal
          if (result?.ErrorCode === "RECIPE_IN_USE") {
            setPendingRecipeData(recipeData);
            setShowUpgradeConfirmModal(true);
            return;
          }
          reset(recipeData);
          setCurrentRecipe(recipeData);
          setNotificationMessage("Recipe updated successfully.");
        },
        onError: onMutationError,
      });
    }
  };

  // Commodities are loaded via useCommoditiesQuery (TanStack Query)
  // Recipe layers are loaded automatically by useRecipeLayersQuery

  const handleSaveNewLayer = (
    commodityId: number | null,
    weight: number,
    chargeNumber: number,
  ) => {
    if (!recipe?.RecipeId && !getValues("RecipeId")) return;

    const recipeId = getValues("RecipeId") || recipe?.RecipeId;
    if (!recipeId || !commodityId) return;

    const newLayer = {
      RecipeId: recipeId,
      CommodityId: commodityId,
      Weight: weight,
      ChargeNumber: chargeNumber,
    };

    upsertLayerMutation.mutate(newLayer, {
      onSuccess: (resp) => {
        const result = resp.data?.tables?.[0]?.rows?.[0];
        // Check if recipe is in use - show confirmation modal
        if (result?.ErrorCode === "RECIPE_IN_USE") {
          setShowRecipeLayerModal(false);
          setPendingNewLayerData({ commodityId, weight, chargeNumber });
          setShowUpgradeConfirmModal(true);
          return;
        }
        const createdLayerId = result?.Id;
        setShowRecipeLayerModal(false);
        setNotificationMessage("New layer added successfully");
        if (createdLayerId) {
          setNewLayerId(createdLayerId);
        }
      },
      onError: onMutationError,
    });
  };

  // Callback for when an existing layer edit triggers RECIPE_IN_USE
  const handleLayerRecipeInUse = (layerData: RecipeLayer) => {
    setPendingLayerUpdateData(layerData);
    setShowUpgradeConfirmModal(true);
  };

  const handleConfirmToggleEnable = () => {
    if (!recipe?.OriginalRecipeId) return;

    setFamilyEnableMutation.mutate(
      {
        originalRecipeId: recipe.OriginalRecipeId,
        isEnable: toggleEnableConfirm.isEnable,
      },
      {
        onError: onMutationError,
        onSettled: () =>
          setToggleEnableConfirm({ open: false, isEnable: false }),
      },
    );
  };

  const handleCancelNewLayer = (layerId: number) => {
    deleteLayerMutation.mutate(layerId, {
      onSuccess: () => {
        setNotificationMessage("Layer creation cancelled");
        setNewLayerId(null);
      },
      onError: onMutationError,
      onSettled: () => {
        // Clear newLayerId regardless of success/error
        setNewLayerId(null);
      },
    });
  };

  // Handle upgrade confirmation: create new version and apply pending changes
  const handleConfirmUpgrade = () => {
    const currentRecipeId =
      pendingRecipeData?.RecipeId ||
      pendingLayerUpdateData?.RecipeId ||
      recipe?.RecipeId ||
      getValues("RecipeId");
    if (!currentRecipeId) return;

    upgradeMutation.mutate(currentRecipeId, {
      onSuccess: (resp) => {
        const newRecipeId = resp.data?.tables?.[0]?.rows?.[0]?.Id;
        if (!newRecipeId) return;

        // Case 1: Pending recipe field changes
        if (pendingRecipeData) {
          // Exclude RecipeName - the upgrade already generated the correct versioned name
          const { RecipeName: _excludedName, ...restPendingData } =
            pendingRecipeData;
          const updatedRecipeData = {
            ...restPendingData,
            RecipeId: newRecipeId,
          } as Recipe;
          upsertRecipeMutation.mutate(updatedRecipeData, {
            onSuccess: () => {
              setNotificationMessage(
                "New version created and changes applied successfully.",
              );
              onHide({
                shouldUpdate: true,
                close: false,
                entityId: newRecipeId,
                keepEditMode: true,
              });
            },
            onError: onMutationError,
            onSettled: () => {
              setShowUpgradeConfirmModal(false);
              setPendingRecipeData(null);
            },
          });
        }
        // Case 2: Pending new layer
        else if (pendingNewLayerData) {
          const newLayer = {
            RecipeId: newRecipeId,
            CommodityId: pendingNewLayerData.commodityId,
            Weight: pendingNewLayerData.weight,
            ChargeNumber: pendingNewLayerData.chargeNumber,
          };
          upsertLayerMutation.mutate(newLayer, {
            onSuccess: (layerResp) => {
              const createdLayerId = layerResp.data?.tables?.[0]?.rows?.[0]?.Id;
              setNotificationMessage(
                "New version created and layer added successfully.",
              );
              onHide({
                shouldUpdate: true,
                close: false,
                entityId: newRecipeId,
              });
              if (createdLayerId) {
                setNewLayerId(createdLayerId);
              }
            },
            onError: onMutationError,
            onSettled: () => {
              setShowUpgradeConfirmModal(false);
              setPendingNewLayerData(null);
            },
          });
        }
        // Case 3: Pending layer update (editing existing layer)
        else if (pendingLayerUpdateData) {
          // The new recipe version already has cloned layers, so we need to find
          // the corresponding layer in the new version and update it
          const updatedLayer = {
            ...pendingLayerUpdateData,
            RecipeId: newRecipeId,
            RecipeLayerId: undefined, // Clear to insert new layer in new version
          };
          upsertLayerMutation.mutate(updatedLayer, {
            onSuccess: () => {
              setNotificationMessage(
                "New version created and layer updated successfully.",
              );
              onHide({
                shouldUpdate: true,
                close: false,
                entityId: newRecipeId,
              });
            },
            onError: onMutationError,
            onSettled: () => {
              setShowUpgradeConfirmModal(false);
              setPendingLayerUpdateData(null);
            },
          });
        }
      },
      onError: () => {
        // Clean up pending state on upgrade failure
        setShowUpgradeConfirmModal(false);
        setPendingRecipeData(null);
        setPendingNewLayerData(null);
        setPendingLayerUpdateData(null);
      },
    });
  };

  const handleCancelUpgrade = () => {
    setShowUpgradeConfirmModal(false);
    setPendingRecipeData(null);
    setPendingNewLayerData(null);
    setPendingLayerUpdateData(null);
    // Reset form to original recipe data
    if (recipe) {
      reset(recipe);
      setCurrentRecipe(recipe);
    }
  };

  const handleConfirmRename = () => {
    if (!pendingRenameValue || !recipe?.RecipeId) return;

    const recipeData = {
      ...getValues(),
      RecipeName: pendingRenameValue,
      RecipeId: recipe.RecipeId,
    } as Recipe;

    upsertRecipeMutation.mutate(recipeData, {
      onSuccess: () => {
        reset(recipeData);
        setCurrentRecipe(recipeData);
        setNotificationMessage("Recipe renamed successfully.");
        setShowRenameConfirmModal(false);
        setPendingRenameValue(null);
      },
      onError: onMutationError,
    });
  };

  const handleCancelRename = () => {
    setShowRenameConfirmModal(false);
    setPendingRenameValue(null);
    // Reset form to original recipe name
    if (recipe) {
      reset(recipe);
    }
  };

  useEffect(() => {
    setSelectedCharge(1);
    setEffectiveRecipeId(recipe?.RecipeId ?? null);
  }, [recipe?.RecipeId]);

  // Only reset form when modal opens or when switching to a different recipe
  // This prevents losing user edits when SignalR invalidates and refetches the same recipe
  useEffect(() => {
    if (modal !== MODAL_STATE.CLOSED) {
      reset(recipe || RECIPE_INITIAL_VALUES);
      setCurrentRecipe(recipe);
      setEffectiveRecipeId(recipe?.RecipeId ?? null);
    }
  }, [modal, recipe?.RecipeId]);

  // No useEffect needed to reset layers on NEW mode — useRecipeLayersQuery
  // returns [] when enabled: false (recipeId is null for new recipes).

  useEffect(() => {
    if (currentRecipe) {
      reset({
        ...currentRecipe,
        IsDuplicate: getValues("IsDuplicate") || false,
      });
    }
  }, [currentRecipe, getValues, reset]);

  return (
    <>
      {recipe === null && modal !== MODAL_STATE.NEW ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography>Select a recipe or create a new one</Typography>
        </Box>
      ) : (
        <form
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          {/* Version Status Badge */}
          <Box
            sx={{
              px: 2,
              pt: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isNewRecipe ? (
              <>
                <Chip
                  label="Draft"
                  color="default"
                  size="small"
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  V1
                </Typography>
              </>
            ) : recipe ? (
              <>
                <Chip
                  label={recipe.IsCurrent ? "Current Version" : "Draft"}
                  color={recipe.IsCurrent ? "success" : "default"}
                  size="small"
                  variant={recipe.IsCurrent ? "filled" : "outlined"}
                />
                {recipe.IsUsed ? (
                  <Chip
                    label="In Use"
                    color="warning"
                    size="small"
                    variant="filled"
                  />
                ) : null}
                {recipe.Version ? (
                  <Typography variant="body2" color="text.secondary">
                    V{recipe.Version}
                  </Typography>
                ) : null}
                {isReadonly ? (
                  <Typography variant="caption" color="text.secondary">
                    — This version is frozen. It has been used in a charge.
                  </Typography>
                ) : null}
              </>
            ) : null}
          </Box>

          {/* Section One: Input Fields */}
          <Box sx={{ px: 2, pt: 2 }}>
            <Grid container spacing={1}>
              <Grid item md={3} xs={12} sm={6}>
                <Controller
                  name="RecipeName"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      label="Name"
                      variant="outlined"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={() => handleFieldBlur("RecipeName", field.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      size="small"
                      fullWidth
                      required
                      error={!!errors.RecipeName}
                      autoFocus={isNewRecipe}
                      disabled={isReadonly || isFamilyInUse}
                    />
                  )}
                />
              </Grid>
              <Grid item md={3} xs={12} sm={6}>
                <Controller
                  name="Author"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Author"
                      variant="outlined"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={() => handleFieldBlur("Author", field.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      size="small"
                      fullWidth
                      error={!!errors.Author}
                      disabled={isNewRecipePending || isReadonly}
                    />
                  )}
                />
              </Grid>

              {!isNewRecipe ? (
                <Grid item md={recipe?.IsCurrent ? 2 : 3} xs={12} sm={6}>
                  <Controller
                    name="CreationTimestamp"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Created"
                        variant="outlined"
                        value={
                          dxtToLocalServerTime(field.value, "MM/dd/yy HH:mm") ??
                          ""
                        }
                        size="small"
                        fullWidth
                        disabled
                      />
                    )}
                  />
                </Grid>
              ) : null}

              {!isNewRecipe ? (
                <Grid item md={recipe?.IsCurrent ? 2 : 3} xs={12} sm={6}>
                  <Controller
                    name="UpdateTimestamp"
                    control={control}
                    render={({ field }) => (
                      <Box sx={{ height: 40 }}>
                        {field.value ? (
                          <TextField
                            label="Updated"
                            variant="outlined"
                            value={
                              dxtToLocalServerTime(
                                field.value,
                                "MM/dd/yy HH:mm",
                              ) ?? ""
                            }
                            size="small"
                            fullWidth
                            disabled
                          />
                        ) : null}
                      </Box>
                    )}
                  />
                </Grid>
              ) : null}

              {/* Enable checkbox only shown for IsCurrent recipes */}
              {recipe?.IsCurrent ? (
                <Grid
                  item
                  md={2}
                  xs={12}
                  sm={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={Boolean(recipe.IsEnable)}
                      color="primary"
                      id="enabled-checkbox"
                      disabled={setFamilyEnableMutation.isPending}
                      onChange={(e) => {
                        if (!recipe.OriginalRecipeId) return;
                        setToggleEnableConfirm({
                          open: true,
                          isEnable: e.target.checked,
                        });
                      }}
                    />
                    <Typography
                      component="label"
                      htmlFor="enabled-checkbox"
                      sx={{ cursor: "pointer" }}
                    >
                      Enable
                    </Typography>
                  </Box>
                </Grid>
              ) : null}

              <Grid item xs={12}>
                <Grid container spacing={2} justifyContent="center">
                  {[1, 2, 3].map((chargeNumber) => (
                    <Grid item xs={12} sm={4} key={chargeNumber}>
                      <Button
                        fullWidth
                        variant={
                          selectedCharge === chargeNumber
                            ? "contained"
                            : "outlined"
                        }
                        color="primary"
                        onClick={() => setSelectedCharge(chargeNumber)}
                        sx={{ minWidth: "100px" }}
                        disabled={isNewRecipePending}
                      >
                        Charge {chargeNumber}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* Section Two: Recipe Layers */}
          <Box
            sx={{
              px: 2,
              py: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  maxHeight: "calc(100vh - 455px)",
                  minHeight: "calc(100vh - 455px)",
                  overflowY: "auto",
                }}
              >
                <Grid
                  container
                  alignItems="center"
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "grey.50",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <Grid
                    item
                    md={2}
                    xs={2}
                    sx={{ padding: "8px 15px", textAlign: "center" }}
                  >
                    <Typography variant="body2">Layer</Typography>
                  </Grid>
                  <Grid
                    item
                    md={5}
                    xs={5}
                    sx={{ padding: "8px 15px", textAlign: "center" }}
                  >
                    <Typography variant="body2">Commodity</Typography>
                  </Grid>
                  <Grid
                    item
                    md={2}
                    xs={2}
                    sx={{ padding: "8px 15px", textAlign: "center" }}
                  >
                    <Typography variant="body2">Weight</Typography>
                  </Grid>
                  <Grid
                    item
                    md={1}
                    xs={1}
                    sx={{ padding: "8px 15px", textAlign: "center" }}
                  >
                    <Typography variant="body2">%</Typography>
                  </Grid>
                  <Grid
                    item
                    md={2}
                    xs={2}
                    sx={{
                      textAlign: "center",
                      display: "flex",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <Tooltip title="Add new layer">
                      <IconButton
                        color="primary"
                        onClick={handleAddLayer}
                        size="small"
                        disabled={isNewRecipePending || isReadonly}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    bgcolor: isNewRecipe ? "grey.50" : "background.paper",
                  }}
                >
                  {recipeLayers.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: "40px 0",
                        color: "text.secondary",
                      }}
                    >
                      <Typography variant="body2">
                        {isNewRecipePending
                          ? "Enter a recipe name and click '+' to add layers"
                          : "No layers added yet"}
                      </Typography>
                    </Box>
                  ) : (
                    <DndProvider backend={HTML5Backend}>
                      {displayLayers
                        ?.filter(
                          (layer) => layer.ChargeNumber === selectedCharge,
                        )
                        .map((layer, index) => (
                          <SortableRecipeLayer
                            key={`layer-${layer.RecipeLayerId}`}
                            index={index}
                            layer={layer}
                            commodities={enabledCommodities}
                            setNotificationMessage={setNotificationMessage}
                            initialEditMode={layer.RecipeLayerId === newLayerId}
                            onEditComplete={() => setNewLayerId(null)}
                            totalWeight={totalWeight}
                            isNewLayer={layer.RecipeLayerId === newLayerId}
                            onCancelNewLayer={handleCancelNewLayer}
                            maxLayerNumber={maxLayerNumber}
                            disabled={isNewRecipePending || isReadonly}
                            onRecipeInUse={handleLayerRecipeInUse}
                            moveLayer={handleMoveLayer}
                            onDragEnd={handleDragEnd}
                            onDragCancel={handleDragCancel}
                          />
                        ))}
                    </DndProvider>
                  )}
                </Box>
              </Box>
              <Grid
                container
                sx={{
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: "grey.50",
                  p: "8px 15px",
                }}
              >
                <Grid item md={6} xs={6}></Grid>
                <Grid item md={4} xs={4} sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Total: {totalWeight}
                  </Typography>
                </Grid>
                <Grid item md={2} xs={2}></Grid>
              </Grid>
            </Box>
          </Box>

          {/* Section Three: Comments */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Controller
              name="Comments"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <CommentsField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={() => handleFieldBlur("Comments", field.value)}
                  fieldError={error}
                  getValues={getValues}
                  rows={{ minRows: 1, maxRows: 1 }}
                  disabled={isNewRecipePending || isReadonly}
                />
              )}
            />
          </Box>
        </form>
      )}
      <RecipeLayerModal
        open={showRecipeLayerModal}
        onClose={() => setShowRecipeLayerModal(false)}
        onSave={handleSaveNewLayer}
        commodities={enabledCommodities}
        isLoading={isLoading}
        isSaving={upsertLayerMutation.isPending}
        title="Add New Recipe Layer"
        recipeId={recipe?.RecipeId || getValues("RecipeId") || null}
        chargeNumber={selectedCharge}
      />
      <NotificationSnackBar
        message={message}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        title="Recipe Error"
        error={error}
        onHide={() => setError("")}
      />
      {/* Rename Family Confirmation Modal */}
      <MesfModal
        open={showRenameConfirmModal}
        handleClose={handleCancelRename}
        title="Rename Recipe Family"
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              mb: 3,
            }}
          >
            <WarningIcon color="warning" sx={{ fontSize: 40, mt: 0.5 }} />
            <Typography variant="body1">
              Renaming this recipe will affect <strong>all versions</strong> in
              this recipe family.
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            All existing versions will be updated with the new name.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancelRename}
              disabled={upsertRecipeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmRename}
              disabled={upsertRecipeMutation.isPending}
            >
              {upsertRecipeMutation.isPending ? "Renaming..." : "Proceed"}
            </Button>
          </Box>
        </Box>
      </MesfModal>
      {/* Recipe In Use - Upgrade Confirmation Modal */}
      <MesfModal
        open={showUpgradeConfirmModal}
        handleClose={handleCancelUpgrade}
        title="Recipe In Use"
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              mb: 3,
            }}
          >
            <WarningIcon color="warning" sx={{ fontSize: 40, mt: 0.5 }} />
            <Typography variant="body1">
              This recipe is currently assigned to active charges and cannot be
              modified directly.
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Would you like to create a <strong>new version</strong> of this
            recipe with your changes?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The current version will remain unchanged for existing charges.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancelUpgrade}
              disabled={upgradeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmUpgrade}
              disabled={upgradeMutation.isPending}
            >
              {upgradeMutation.isPending ? "Creating..." : "Create New Version"}
            </Button>
          </Box>
        </Box>
      </MesfModal>
      {/* Enable/Disable Family Confirmation Modal */}
      <MesfModal
        open={toggleEnableConfirm.open}
        handleClose={() =>
          setToggleEnableConfirm({ open: false, isEnable: false })
        }
        title={
          toggleEnableConfirm.isEnable
            ? "ENABLE RECIPE FAMILY"
            : "DISABLE RECIPE FAMILY"
        }
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You are about to{" "}
            {toggleEnableConfirm.isEnable ? "enable" : "disable"} recipe family{" "}
            <strong>{recipe?.RecipeName}</strong>. This recipe will{" "}
            {toggleEnableConfirm.isEnable ? "appear" : "no longer appear"} in
            the Crane app. Do you want to proceed?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() =>
                setToggleEnableConfirm({ open: false, isEnable: false })
              }
              disabled={setFamilyEnableMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmToggleEnable}
              disabled={setFamilyEnableMutation.isPending}
            >
              {toggleEnableConfirm.isEnable ? "ENABLE" : "DISABLE"}
            </Button>
          </Box>
        </Box>
      </MesfModal>
    </>
  );
};
